import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { ArrowRight, Check, Plug } from '../components/Icons';

const REGIONS = [
  { value: 'EMEA_1', label: 'EU (emea-1)' },
  { value: 'AMER_1', label: 'US (amer-1)' },
  { value: 'APAC_1', label: 'Singapore (apac-1)' },
];

export default function IntegrationsScreen() {
  const [clientId, setClientId]         = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [region, setRegion]             = useState('EMEA_1');
  const [connected, setConnected]       = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [expanded, setExpanded]         = useState(false);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError]               = useState(null);
  const [success, setSuccess]           = useState(null);

  useEffect(() => {
    api.getJumioConfig()
      .then(data => {
        if (data?.connected) {
          setConnected(true);
          setClientId(data.clientId || '');
          setClientSecret(data.clientSecret || '');
          setRegion(data.region || 'EMEA_1');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    if (!clientId.trim() || !clientSecret.trim()) { setError('Client ID and Secret are required.'); return; }
    setSaving(true);
    try {
      const data = await api.saveJumioConfig({ clientId: clientId.trim(), clientSecret: clientSecret.trim(), region });
      setConnected(true);
      setClientId(data.clientId || clientId);
      setClientSecret(data.clientSecret || '••••••••');
      setShowForm(false);
      setExpanded(false);
      setSuccess('Jumio connected.');
    } catch (e) { setError(e.message || 'Failed to connect.'); }
    finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Jumio?')) return;
    setError(null); setSuccess(null); setDisconnecting(true);
    try {
      await api.deleteJumioConfig();
      setConnected(false); setShowForm(false); setClientId(''); setClientSecret('');
      setSuccess('Jumio disconnected.');
    } catch (e) { setError(e.message || 'Failed to disconnect.'); }
    finally { setDisconnecting(false); }
  };

  if (loading) return <p className="vt-muted">Loading…</p>;

  const editing = !connected || showForm;
  const formOpen = showForm || (!connected && expanded);

  return (
    <div>
      <div className="vt-pageHead">
        <div className="vt-eyebrow">Connections</div>
        <h1 className="vt-pageTitle">Integrations</h1>
        <p className="vt-pageSub">Send completed verifications to the tools you already use.</p>
      </div>

      {error   && <div className="vt-alert vt-alert--error">{error}</div>}
      {success && <div className="vt-alert vt-alert--success"><Check size={16} /> {success}</div>}

      <section className="vt-panel">
        <div className="vt-panelHead">
          <div>
            <h2 className="vt-panelTitle">Providers</h2>
            <p className="vt-panelSub">{connected ? '1 connected' : 'None connected'}</p>
          </div>
        </div>
        <div className="vt-list">
          <div className="vt-row">
            <span className={['vt-tile', connected ? 'vt-tile--dark' : ''].filter(Boolean).join(' ')}>J</span>
            <div className="vt-rowBody">
              <div className="vt-rowTitle">
                Jumio
                {connected && <span className="vt-tag vt-tag--success vt-tag--dot">Connected</span>}
              </div>
              <div className="vt-rowMeta">
                Posts each verification to Jumio, linked by <code>userReference</code>.{' '}
                <a href="https://docs.veritage.io/wallet-hub/v1/jumio" target="_blank" rel="noreferrer">How it works</a>
              </div>
            </div>
            <div className="vt-rowActions">
              {connected && !showForm && (
                <>
                  <button type="button" className="vt-btn vt-btn--ghostPill vt-btn--sm" onClick={() => { setClientSecret(''); setShowForm(true); }}>Edit</button>
                  <button type="button" className="vt-btn vt-btn--ghostPill vt-btn--sm" onClick={handleDisconnect} disabled={disconnecting} style={{ color: '#b91c1c' }}>
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </>
              )}
              {!connected && !expanded && (
                <button type="button" className="vt-btn vt-btn--primary vt-btn--sm" onClick={() => setExpanded(true)}>Connect</button>
              )}
            </div>
          </div>

          {connected && !showForm && (
            <div className="vt-row" style={{ display: 'block' }}>
              <dl className="vt-defList" style={{ borderTop: 0 }}>
                <Field label="Client ID"     value={clientId} mono />
                <Field label="Client secret" value={clientSecret} mono />
                <Field label="Region"        value={REGIONS.find(r => r.value === region)?.label || region} />
              </dl>
            </div>
          )}

          {editing && formOpen && (
            <div className="vt-row" style={{ display: 'block', paddingTop: 20, paddingBottom: 22 }}>
              <form onSubmit={handleConnect} noValidate>
                <div className="vt-grid3" style={{ alignItems: 'start' }}>
                  <FormField id="client-id" label="Client ID" hint="Jumio Portal → Settings → API credentials">
                    <input id="client-id" className="vt-input" type="text" value={clientId} onChange={e => setClientId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </FormField>
                  <FormField id="client-secret" label="Client secret">
                    <input id="client-secret" className="vt-input" type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)} placeholder={showForm ? 'Leave blank to keep existing' : ''} />
                  </FormField>
                  <FormField id="region" label="Region">
                    <select id="region" className="vt-input" value={region} onChange={e => setRegion(e.target.value)}>
                      {REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </FormField>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
                  <button type="submit" disabled={saving} className="vt-btn vt-btn--primary">
                    {saving ? 'Connecting…' : showForm ? 'Save changes' : 'Connect Jumio'}
                    <span className="vt-btnArrow"><ArrowRight size={13} /></span>
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setExpanded(false); }} className="vt-btn vt-btn--ghostPill">Cancel</button>
                </div>
              </form>
            </div>
          )}

          <div className="vt-row vt-row--muted">
            <span className="vt-tile"><Plug /></span>
            <div className="vt-rowBody">
              <div className="vt-rowTitle">More providers coming</div>
              <div className="vt-rowMeta">
                CRM, case management and AML. Need one? <a href="mailto:team@veritage.io">team@veritage.io</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="vt-defRow">
      <dt>{label}</dt>
      <dd style={{ fontFamily: mono ? 'var(--vt-font-mono)' : 'inherit' }}>{value}</dd>
    </div>
  );
}

function FormField({ id, label, hint, children }) {
  return (
    <div className="vt-field">
      <label htmlFor={id} className="vt-fieldLabel">{label}</label>
      {children}
      {hint && <span className="vt-fieldHint">{hint}</span>}
    </div>
  );
}
